package com.dotdrives.web.pages.donations

import com.dotdrives.controller.YearEndReceiptsController
import com.dotdrives.controller.YearEndReceiptsUiState
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface YearEndReceiptsDialogProps : Props {
    var orgId: String
    var userId: String
    var currentUserRole: String
    var controller: YearEndReceiptsController
    var state: YearEndReceiptsUiState
    var onClose: () -> Unit
}

val YearEndReceiptsDialog = FC<YearEndReceiptsDialogProps> { props ->
    com.dotdrives.web.ui.useDialogFocus(true, "year-end-dialog", props.onClose)
    val state = props.state
    val controller = props.controller
    val summary = state.summary
    val (previewOpen, setPreviewOpen) = useState(false)

    div {
        className = ClassName("donations-dialog-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onClose() }

        div {
            className = ClassName("donations-yearend-dialog")
            id = web.dom.ElementId("year-end-dialog")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "Year-end receipts"
            onClick = { it.stopPropagation() }

            button {
                type = ButtonType.buttonType
                className = ClassName("donations-yearend-close")
                ariaLabel = "Close"
                onClick = { props.onClose() }
                +"✕"
            }
            div {
                className = ClassName("donations-yearend-head")
                div { className = ClassName("donations-yearend-icon"); +"🧾" }
                div {
                    div { className = ClassName("donations-yearend-title"); +"Year-end receipts · ${state.year ?: ""}" }
                    div { className = ClassName("donations-yearend-subtitle"); +"Save year-end snapshots and track their documents and email." }
                }
            }

            state.error?.let { error ->
                DotBanner { message = error; onRetry = props.controller::retry; className = "donations-inline-banner" }
            }

            if (state.loading && summary == null) {
                repeat(3) { DotSkeleton { height = "60px" } }
            } else if (summary != null) {
                div {
                    className = ClassName("donations-yearend-stats")
                    stat("GIVERS", summary.giverCount.toString())
                    stat("GIFTS", summary.giftCount.toString())
                    stat("TOTAL", formatMoneyCompact(summary.totalAmount))
                }
                div {
                    className = ClassName("donations-yearend-checklist")
                    span { +"✓ ${summary.pendingCount} statement${if (summary.pendingCount == 1) "" else "s"} need a saved snapshot" }
                    if (summary.alreadyGeneratedGiverIds.isNotEmpty()) {
                        span { +"✓ ${summary.alreadyGeneratedGiverIds.size} snapshots already saved for ${state.year}" }
                    }
                    state.sentCount?.let { count ->
                        span { +"✓ Saved and queued $count new statement${if (count == 1) "" else "s"} just now" }
                    }
                    span { +"Email is queued after the snapshot is saved. Delivery status below records worker progress and actual provider acceptance." }
                }
                if (previewOpen) {
                    div {
                        className = ClassName("donations-yearend-preview")
                        val top = summary.drafts.firstOrNull()
                        if (top == null) {
                            +"No givers have a gift on record for ${state.year} yet."
                        } else {
                            +"${top.giverDisplayName} · ${top.giftCount} gift${if (top.giftCount == 1) "" else "s"} · ${formatMoneyFull(top.totalAmount)} total"
                        }
                    }
                }
                div {
                    className = ClassName("donations-yearend-actions")
                    DotButton {
                        text = "View draft summary"
                        variant = DotButtonVariant.Tinted
                        onClick = { setPreviewOpen(!previewOpen) }
                    }
                    DotButton {
                        text = "Save and queue ${summary.pendingCount} →"
                        onClick = controller::send
                        loading = state.sending
                        enabled = summary.pendingCount > 0 && !state.sending
                    }
                }
            }
            StatementDeliveryPanel {
                orgId = props.orgId; userId = props.userId; role = props.currentUserRole
                statements = summary?.statements.orEmpty()
            }
        }
    }
}

private fun react.ChildrenBuilder.stat(label: String, value: String) {
    div {
        className = ClassName("donations-yearend-stat")
        div { className = ClassName("donations-yearend-stat-label"); +label }
        div { className = ClassName("donations-yearend-stat-value"); +value }
    }
}
